import React, { useRef } from 'react';
import classNames from 'classnames';
import { buildUseStyles, useTheme } from 'Components/ComponentLibrary/ExternalLibraries/Libraries/MaterialV4/styles';
import Box from 'Components/ComponentLibrary/ExternalLibraries/Libraries/MaterialV4/Box';
import getTemplate from 'Views/Page/Content/RightSidebar/Content/Templates';
import { useRightSidebarContext } from 'Views/Page/Content/RightSidebar/RightSidebarContext';
import { localStorageMap } from 'Consts/localStorage';
import DataContainer from 'Components/UI/DataContainer';
import ResizableBox from 'Components/ComponentLibrary/Atoms/ResizableBox';
import PropTypes from 'prop-types';
import IconButton from 'Components/ComponentLibrary/ExternalLibraries/Libraries/MaterialV4/IconButton';
import CloseIcon from '@material-ui/icons/Close';
import Header from './Header';

const contentContainerPadding = 16; // padding of the content container above the guided layout wrapper
const useStyles = buildUseStyles('RightSidebarContent', ((theme) => ({
  root: {
    position: 'relative',
    flexShrink: 0,
  },
  rootHidden: {
    display: 'none',
  },
  content: {
    overflowX: 'hidden',
    padding: theme.spacing(2),
    flexGrow: 1,
  },
  sidebarContainer: {
    position: 'relative',
    display: 'flex',
    flexShrink: 0,
    '& .MuiPaper-root': { // select accordion papers
      backgroundColor: theme.palette.facet.lightGray, // useBoneBackgroundColor alternative
    },
    height: `calc(100vh - ${theme.heights.advisorNavigationHeight + contentContainerPadding + contentContainerPadding}px)`, // top nav, top padding, bottom padding
  },
  flexibleChild: {
    display: 'flex',
    flexDirection: 'column',
    flex: 1,
    '& > *': {
      display: 'flex !important', // Needed to override FDContentContainer's display:block
      flexDirection: 'column',
      flex: 1,
    },
    // And explicitly revert the last child so it doesn't inherit the generic rule
    '&:first-child > :last-child': { // there is a hidden MetaFormInline-hide that we don't want to give flexibleChild to
      display: 'none !important',
    },
  },
  sidebarContainerContent: {
    overflowX: 'hidden',
    overflowY: 'auto',
    padding: theme.spacing(1),
  },
  closeIconWrapper: {
    maxHeight: theme.heights.advisorNavigationHeight,
    padding: theme.spacing(0, 2),
    flexDirection: 'row',
    justifyContent: 'flex-end', // make icon go to end of container
    '& .MuiIconButton-root': {
      color: theme.palette.text.secondary, // change icon color
    },
  },
})));

const RightSidebarContent = ({
  title, hideHeader, onPickNewSize, alwaysShowDivider, ...rest
}) => {
  const classes = useStyles();
  const { rightSidebarCollection, showRightSidebarCollection } = useRightSidebarContext();
  const resizableRef = useRef(null);
  const theme = useTheme();

  // Memoize which template component to use without tripping rules-of-hooks
  const Template = React.useMemo(() => getTemplate(rightSidebarCollection), [rightSidebarCollection]);

  if (!rightSidebarCollection) {
    return null;
  }

  const isRoadmapExplorerSidebar = onPickNewSize !== undefined;
  const onClickCloseIconCollapse = () => {
    resizableRef.current?.toggleCollapsed();
  };

  return (
    <ResizableBox
      className={classNames(classes.root, {
        [classes.rootHidden]: !showRightSidebarCollection,
        [classes.sidebarContainer]: isRoadmapExplorerSidebar,
      })}
      ref={resizableRef}
      side={'left'}
      localStorageName={localStorageMap.rightColumnWidth}
      minSize={theme.dimensions.sm}
      onPickNewSize={onPickNewSize}
      startCollapsed={isRoadmapExplorerSidebar}
      alwaysShowDivider={alwaysShowDivider}
    >
      {showRightSidebarCollection ? (
        <>
          {!hideHeader && (<Header title={title} />)}
          <Box className={isRoadmapExplorerSidebar ? classNames(classes.sidebarContainerContent, classes.flexibleChild) : classes.content}>
            {hideHeader && (
            <Box className={classes.closeIconWrapper}>
              <IconButton
                onClick={onClickCloseIconCollapse}
                icon={CloseIcon}
                title={'Close'}
              />
            </Box>
            )}
            <DataContainer
              component={Template}
              componentProps={{
                collection: rightSidebarCollection,
                ...rest,
              }}
              loading={!rightSidebarCollection}
            />
          </Box>
        </>
      ) : null}
    </ResizableBox>
  );
};

RightSidebarContent.defaultProps = {
  title: 'Preview',
  hideHeader: false,
};

RightSidebarContent.propTypes = {
  title: PropTypes.string,
  hideHeader: PropTypes.bool,
  onPickNewSize: PropTypes.func,
  props: PropTypes.object,
};

export default RightSidebarContent;
